import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UploadedGeoImageService as UploadedGeoImageApiService } from '@6grain/masofa-monolith-api-client/common/api/uploadedGeoImage.service';
import { environment } from '../../../../../environments/environment';
import { GeoImageKind, GeoImageRejectionReason } from '../geo-image-upload-modal/geo-image-upload.models';
import {
  UploadedGeoImageGalleryRequest,
  UploadedGeoImageGalleryResponse,
  UploadedGeoImageListItem,
  UploadedGeoImageMetadata,
  UploadedGeoImageUpdateRequest
} from './uploaded-geo-image.models';

/**
 * The server's actual wire shape is camelCase: `AddNewtonsoftJson()` in
 * `ServiceCollectionExtention.cs` falls back to its own default `CamelCasePropertyNamesContractResolver`
 * for every property this configuration doesn't otherwise convert, so these responses already match
 * these camelCase interfaces field-for-field — confirmed by logging the raw response from a running
 * instance. No PascalCase-to-camelCase mapping is needed here.
 */
export interface InspectGeoImageResponse {
  accepted: boolean;
  imageKind: GeoImageKind | null;
  footprintWkt: string | null;
  captureDateUtc: string | null;
  rejectionReason: GeoImageRejectionReason | null;
}

export interface UploadGeoImageBatchItemPayload {
  title: string;
  description?: string | null;
  tagIds: string[];
  /** WKT "POINT (lon lat)" — only for a manually coordinate-entered photo (FR-004). */
  pointJson?: string | null;
}

export interface UploadGeoImageBatchCreatedItem {
  index: number;
  id: string;
  imageKind: GeoImageKind;
}

export interface UploadGeoImageBatchSkippedItem {
  index: number;
  reason: string;
}

export interface UploadGeoImageBatchResponse {
  created: UploadGeoImageBatchCreatedItem[];
  skipped: UploadGeoImageBatchSkippedItem[];
}

/** What the URL builders need from a gallery row. */
type UploadedGeoImageFileRef = Pick<UploadedGeoImageListItem, 'fileStorageId' | 'previewFileStorageId' | 'imageKind'>;

/** Wraps the manual geo-image endpoints through the generated client. File URLs stay local because they are templates. */
@Injectable({
  providedIn: 'root'
})
export class UploadedGeoImageService {
  constructor(private readonly generatedApi: UploadedGeoImageApiService) {}

  /** Preview-stage status for a GeoTIFF, without saving anything (contract `Inspect`). */
  inspect(file: File): Observable<InspectGeoImageResponse> {
    return this.generatedApi.commonUploadedGeoImageInspectPost(file) as Observable<InspectGeoImageResponse>;
  }

  /** Confirms "Start processing" for the checked batch (contract `UploadBatch`). */
  uploadBatch(files: File[], items: UploadGeoImageBatchItemPayload[]): Observable<UploadGeoImageBatchResponse> {
    return this.generatedApi.commonUploadedGeoImageUploadBatchPost(files, JSON.stringify(items)) as Observable<UploadGeoImageBatchResponse>;
  }

  /** The shared gallery, filtered (contract `GetGallery`). */
  getGallery(request: UploadedGeoImageGalleryRequest): Observable<UploadedGeoImageGalleryResponse> {
    return this.generatedApi.commonUploadedGeoImageGetGalleryPost(request) as Observable<UploadedGeoImageGalleryResponse>;
  }

  /** Filter options: regions containing images, active tags, and date bounds (contract `GetMetadata`). */
  getMetadata(): Observable<UploadedGeoImageMetadata> {
    return this.generatedApi.commonUploadedGeoImageGetMetadataGet() as Observable<UploadedGeoImageMetadata>;
  }

  /** Edits title, description, and tags; resolves to the refreshed gallery row (contract `UpdateUploadedGeoImage`). */
  update(request: UploadedGeoImageUpdateRequest): Observable<UploadedGeoImageListItem> {
    return this.generatedApi.commonUploadedGeoImageUpdateUploadedGeoImagePut(request) as Observable<UploadedGeoImageListItem>;
  }

  /** Soft-deletes an image: it disappears from the gallery but its file is kept. */
  delete(id: string): Observable<boolean> {
    return this.generatedApi.commonUploadedGeoImageDeleteIdDelete(id);
  }

  /**
   * A thumbnail of the image, or `null` when there is nothing a browser can draw. A photo is its own thumbnail; a
   * raster is drawn from its generated preview, and without one only the GeoTIFF exists, which the server cannot
   * resize and a browser cannot decode, so requesting it would download the whole file for nothing.
   */
  getThumbnailUrl(item: UploadedGeoImageFileRef, width: number, height: number): string | null {
    const fileId = this.drawableFileId(item);
    return fileId ? `${environment.api}/common/FileStorageItem/GetFile/${fileId}?width=${width}&height=${height}&mode=crop` : null;
  }

  /** The original photo, or a raster's preview; `null` for a raster that has no preview (see {@link getThumbnailUrl}). */
  getFullImageUrl(item: UploadedGeoImageFileRef): string | null {
    const fileId = this.drawableFileId(item);
    return fileId ? `${environment.api}/common/FileStorageItem/GetFile/${fileId}` : null;
  }

  private drawableFileId(item: UploadedGeoImageFileRef): string | null {
    if (item.previewFileStorageId) {
      return item.previewFileStorageId;
    }
    return item.imageKind === 'Raster' ? null : item.fileStorageId;
  }
}
